import { Component, JSX } from "solid-js";
import { A } from "@solidjs/router";

const LAST_UPDATED = "20 August 2026";
const SUPPORT_EMAIL = "support@ratingraces.pro";

const LegalPage: Component<{ eyebrow: string; title: string; intro: string; children: JSX.Element }> = (props) => (
  <article class="legal-page">
    <div class="page-eyebrow">{props.eyebrow}</div>
    <h1>{props.title}</h1>
    <p class="legal-updated">Last updated: {LAST_UPDATED}</p>
    <p class="legal-intro">{props.intro}</p>
    {props.children}
    <section class="legal-contact">
      <h2>Contact</h2>
      <address>
        Dannan Kelly<br />
        23 Rathdrum, Virginia, Co. Cavan, Ireland<br />
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br />
        <a href="tel:+353830674720">+353 83 067 4720</a>
      </address>
    </section>
  </article>
);

export const PrivacyPolicy: Component = () => (
  <LegalPage
    eyebrow="LEGAL"
    title="Privacy Policy"
    intro="This policy explains how personal data is handled when you visit ratingraces.pro, create an account, use Rating Races Pro, or purchase a subscription."
  >
    <section>
      <h2>1. Who controls your data</h2>
      <p>Dannan Kelly, the operator of Rating Races Pro, is the data controller for personal data processed to operate the service. The contact details at the end of this policy can be used for any privacy request.</p>
    </section>
    <section>
      <h2>2. Data we process</h2>
      <ul>
        <li><strong>Account data:</strong> your email address, account name, password hash, account identifier and authentication-session information.</li>
        <li><strong>Subscription data:</strong> subscription and product identifiers, status, dates and the email associated with your Polar checkout. Rating Races Pro does not receive or store your full payment-card number.</li>
        <li><strong>Service and technical data:</strong> requests made to the service, security and diagnostic records, IP address, browser or device information where recorded by the hosting infrastructure, and interactions needed to provide account access.</li>
        <li><strong>Communications:</strong> information you provide when contacting support or exercising a legal right.</li>
      </ul>
    </section>
    <section>
      <h2>3. Why we process it</h2>
      <ul>
        <li>To create and secure your account, provide requested features and manage membership under our contract with you.</li>
        <li>To confirm subscription status, provide paid access and respond to billing or support issues under our contract with you.</li>
        <li>To protect the service, prevent misuse, diagnose faults and maintain records necessary for our legitimate interests in operating a secure service.</li>
        <li>To keep tax, accounting, consumer-rights and compliance records where required by law.</li>
        <li>To establish, exercise or defend legal claims where necessary.</li>
      </ul>
      <p>We do not sell personal data and do not use it for third-party behavioural advertising.</p>
    </section>
    <section>
      <h2>4. Who receives data</h2>
      <p>Data is shared only where needed with service providers supporting authentication, managed database and hosting infrastructure, and with Polar for checkout, payment, tax, invoicing, subscription and refund administration. Polar acts as merchant of record for purchases made through its checkout and processes buyer information under its own buyer terms and privacy policy.</p>
      <p>We may also disclose information where required by law, to protect legal rights, or in connection with a lawful transfer of the service. Providers may process data outside the European Economic Area. Where GDPR requires it, an approved transfer mechanism or another lawful safeguard must be used.</p>
    </section>
    <section>
      <h2>5. Retention</h2>
      <p>Account and subscription-access data is generally retained while your account is active and for a limited period afterwards where needed for security, disputes or legal claims. Financial and transaction-related records are retained for the period required by applicable tax, accounting and consumer law. Security logs are retained only as long as reasonably necessary for security and diagnostics. When data is no longer required, it is deleted or anonymised.</p>
    </section>
    <section>
      <h2>6. Your rights</h2>
      <p>Subject to GDPR conditions and exceptions, you may request access, correction, erasure, restriction, portability or object to processing based on legitimate interests. Where processing relies on consent, you may withdraw it without affecting earlier lawful processing. We may need to verify your identity before completing a request.</p>
      <p>You may complain to Ireland’s Data Protection Commission at <a href="https://www.dataprotection.ie/" target="_blank" rel="noreferrer">dataprotection.ie</a>. Please contact us first if you would like us to resolve an issue directly.</p>
    </section>
    <section>
      <h2>7. Automated analysis and children</h2>
      <p>The racing model produces statistical outputs about races and runners; it does not make decisions about users that produce legal or similarly significant effects. The service is intended only for people aged 18 or over, and we do not knowingly collect children’s data.</p>
    </section>
    <section>
      <h2>8. Security and changes</h2>
      <p>Reasonable technical and organisational safeguards are used, but no internet service can promise absolute security. We may update this policy when the service or legal requirements change. Material changes will be communicated where required.</p>
    </section>
  </LegalPage>
);

export const TermsOfUse: Component = () => (
  <LegalPage
    eyebrow="LEGAL"
    title="Terms of Use"
    intro="These terms govern your use of Rating Races Pro. By creating an account or using the service, you agree to them."
  >
    <section>
      <h2>1. Operator and eligibility</h2>
      <p>Rating Races Pro is operated by Dannan Kelly in Ireland. You must be at least 18 years old and legally capable of entering a contract to use the service.</p>
    </section>
    <section>
      <h2>2. Statistical information only</h2>
      <p>Rating Races Pro is a mathematical and statistical analysis tool. It does not provide betting advice, gambling advice, sports picks, financial advice or recommendations. Outputs are estimates based on available data, may be incomplete or inaccurate, and do not guarantee any result. You decide independently whether and how to use the information and remain solely responsible for your decisions, legal compliance and any losses.</p>
    </section>
    <section>
      <h2>3. Accounts</h2>
      <p>You must provide accurate information, keep your password confidential and notify us promptly of suspected unauthorised use. Accounts are personal and may not be sold, shared or used to bypass access limits. We may suspend access where reasonably necessary to investigate misuse, protect the service or comply with law.</p>
    </section>
    <section>
      <h2>4. Subscriptions and payment</h2>
      <p>The Monthly plan is €29.99 per month and the Annual plan is €199.99 per year unless a different price is clearly shown before purchase. Subscriptions renew automatically for the same billing period until cancelled. USD amounts displayed on this site are estimates; checkout and billing are in EUR unless Polar states otherwise.</p>
      <p>Polar acts as merchant of record and authorised reseller. Its checkout shows the final price, applicable tax, renewal frequency and payment terms. By purchasing, you also agree to Polar’s applicable buyer terms. See our <A href="/refunds">Cancellation and Refund Policy</A> before subscribing.</p>
    </section>
    <section>
      <h2>5. Service and acceptable use</h2>
      <p>We will provide the digital service with reasonable care and skill and in accordance with applicable consumer law. Availability, data feeds and model outputs may change or be interrupted for maintenance, security, supplier changes or circumstances outside reasonable control.</p>
      <p>You must not misuse the service, interfere with security, scrape or systematically extract protected content, reverse engineer restricted components, resell access, use another person’s account, or use the service unlawfully.</p>
    </section>
    <section>
      <h2>6. Intellectual property</h2>
      <p>The software, presentation, model outputs, branding and original content are owned by or licensed to Rating Races Pro. Your subscription grants a limited, personal, non-exclusive, non-transferable right to use the service while your account remains authorised. Third-party racing and market data remains subject to its owners’ rights.</p>
    </section>
    <section>
      <h2>7. Liability and statutory rights</h2>
      <p>Nothing in these terms excludes liability that cannot legally be excluded or limits your mandatory rights under Irish or EU consumer law. Subject to that rule, we are not responsible for decisions made from model outputs, gambling losses, indirect or consequential loss, or failures caused by third-party data or services outside our reasonable control.</p>
      <p>If the digital service does not conform to the contract, you may have statutory rights to correction, a price reduction, termination or a refund. A disclaimer does not remove those rights.</p>
    </section>
    <section>
      <h2>8. Ending access and changes</h2>
      <p>You may stop using a free account at any time and request deletion by contacting support. Subscription cancellation is explained in the Cancellation and Refund Policy. We may terminate an account for a material breach, fraud, unlawful conduct or a serious security risk, subject to applicable law.</p>
      <p>We may update these terms for legal, security or service reasons. Material changes will be notified where required. Changes do not remove rights already accrued.</p>
    </section>
    <section>
      <h2>9. Governing law and disputes</h2>
      <p>These terms are governed by Irish law. If you are a consumer, this does not deprive you of mandatory protections available in your country of residence. Please contact support first so we can try to resolve any complaint.</p>
    </section>
  </LegalPage>
);

export const CookiePolicy: Component = () => (
  <LegalPage
    eyebrow="LEGAL"
    title="Cookie Policy"
    intro="Rating Races Pro currently uses only storage that is necessary to provide account authentication and security."
  >
    <section>
      <h2>1. What cookies are</h2>
      <p>Cookies are small pieces of data stored by a browser. Similar technologies can also store settings on a device. This policy explains the technologies used directly by Rating Races Pro.</p>
    </section>
    <section>
      <h2>2. Strictly necessary authentication</h2>
      <p>When you sign in, Better Auth sets a first-party session cookie under the ratingraces.pro domain. It is used to keep you signed in, verify requests and protect account access. The service cannot provide signed-in features without it, so it is treated as strictly necessary. Signing out clears or invalidates the session.</p>
    </section>
    <section>
      <h2>3. Analytics and advertising</h2>
      <p>Rating Races Pro does not currently set analytics, advertising or cross-site tracking cookies. The light/dark appearance control is not stored persistently on your device. If optional cookies or similar storage are introduced, this policy and the consent controls will be updated before those technologies are enabled.</p>
    </section>
    <section>
      <h2>4. Polar checkout</h2>
      <p>Choosing a paid plan sends you to Polar’s separate checkout website. Polar may use cookies and similar technologies under its own policies to process payment, prevent fraud and manage subscriptions. Those technologies are controlled by Polar on its domain, not set by Rating Races Pro.</p>
    </section>
    <section>
      <h2>5. Browser controls</h2>
      <p>You can delete or block cookies in your browser settings. Blocking the necessary session cookie will prevent login and paid account features from working. Contact us if you have questions about a technology used by the service.</p>
    </section>
  </LegalPage>
);

export const RefundPolicy: Component = () => (
  <LegalPage
    eyebrow="CUSTOMER INFORMATION"
    title="Cancellation and Refund Policy"
    intro="This page explains recurring billing, how to cancel, and the statutory rights that may apply to your digital-service purchase."
  >
    <section>
      <h2>1. Recurring subscriptions</h2>
      <p>The Monthly plan renews each month at €29.99 and the Annual plan renews each year at €199.99, unless the price shown and accepted at checkout is different. Renewal continues until cancellation. Polar displays the final total, tax and billing frequency before payment.</p>
    </section>
    <section>
      <h2>2. How to cancel</h2>
      <p>You can cancel through the Polar customer portal linked from your Polar receipt or account communications. You may also email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for assistance. Unless a refund or statutory termination applies, cancellation stops the next renewal and access continues until the end of the paid billing period.</p>
    </section>
    <section>
      <h2>3. Fourteen-day withdrawal right</h2>
      <p>If you are an EU consumer purchasing online, you generally have 14 days from the contract date to withdraw without giving a reason. To exercise that right, send a clear statement with your account email and purchase details to support. If you requested immediate service during the withdrawal period, applicable law may allow a proportionate charge for service supplied before withdrawal. Your statutory rights are not waived except where the law expressly permits and all required conditions have been met.</p>
    </section>
    <section>
      <h2>4. Faulty or unavailable service</h2>
      <p>If the digital service is not supplied or does not conform to the contract, contact us promptly. Irish and EU consumer law may entitle you to have the problem corrected, receive a proportionate price reduction, terminate the contract or receive a refund. Approved refunds will be processed through Polar to the original payment method, normally within the legally required period.</p>
    </section>
    <section>
      <h2>5. Request information</h2>
      <p>Include your account email, Polar order or receipt reference, purchase date, plan and the reason for your request. We may ask for information necessary to locate and verify the purchase. This policy does not restrict any mandatory consumer remedy.</p>
    </section>
    <section>
      <h2>6. Model withdrawal form</h2>
      <p>You may copy and email the following notice. Use of this exact format is not required:</p>
      <div class="legal-template">
        To: Dannan Kelly, 23 Rathdrum, Virginia, Co. Cavan, Ireland — support@ratingraces.pro<br /><br />
        I give notice that I withdraw from my contract for the following Rating Races Pro subscription:<br />
        Plan and Polar order reference: ____________________<br />
        Ordered on: ____________________<br />
        Consumer name: ____________________<br />
        Account email: ____________________<br />
        Address: ____________________<br />
        Date: ____________________
      </div>
    </section>
  </LegalPage>
);
